<template>
    <div class="uploader-photo-step">
        <div class="uploader-photo-guide">
            <span class="uploader-guide-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="9" cy="9" r="2"/><path d="m3 17 5-5 4 4 4-6 5 7"/></svg></span>
            <div><strong>{{ $t('design_main_tip_title') }}</strong><p>{{ $t('design_main_tip') }}</p></div>
        </div>
        <div id="header-upload"></div>
        <div v-if="uploadError" style="max-width: 1200px; width:100%;" class="alert-uploader" role="alert">{{ uploadError }}</div>
        <div style="max-width: 1200px; width:100%;" class="alert-uploader" role="alert" v-show="!this.isValid">{{ $t('alert_no_header') }}</div>
        <div class="uploader-actions">
            <button type="button" class="btn-uploader" v-on:click="checkForm()">{{ $t('design_next_tiles') }}<span aria-hidden="true">→</span></button>
        </div>
    </div>
</template>

<script>
    import * as types from "@/store/mutation-types";
    import * as Constants from "../constants";
    import XHRUpload from "@uppy/xhr-upload";
    import Dashboard from "@uppy/dashboard";
    import Uppy from "@uppy/core";
    import axios from "axios";
    import {markRaw} from "vue";
    import {Language} from "@/i18n";
    import {addBalancedImageCompression} from "@/services/image-compression.service";
    import UploadLog from "@/services/upload-log.service";
    import {limitDashboardFocusToKeyboard} from "@/services/dashboard-focus.service";
    import {
        describeFile,
        getImageValidationErrorKey,
        getUppyAllowedFileTypes,
        IMAGE_UPLOAD_RESTRICTIONS,
        validateImageFile,
        validateImageMetadata
    } from "@/services/image-validation.service";
    export default {
        name: "StepOne",
        data: function() {
            return {
                uppy: null,
                isValid: true,
                uploadError: null,
                validationQueue: Promise.resolve(),
                pendingValidations: 0,
            }
        },
        methods: {
            showUploadError: function(messageKey) {
                this.uploadError = this.$t(messageKey);
            },
            validateAndUpload: function(files) {
                this.pendingValidations++;
                this.validationQueue = this.validationQueue.then(async () => {
                    const localFiles = files.filter(file => file.source !== 'Remote' && !file.progress.uploadComplete);
                    const validatedFiles = await Promise.all(localFiles.map(async file => ({
                        file,
                        errorKey: await validateImageFile(file, (error, details) => {
                            UploadLog.log('header_file_read_error', {
                                file: describeFile(file),
                                name: error.name,
                                message: error.message,
                                ...details
                            });
                        })
                    })));
                    const validFiles = validatedFiles.filter(result => !result.errorKey);

                    validatedFiles
                        .filter(result => result.errorKey)
                        .forEach(result => {
                            this.showUploadError(result.errorKey);
                            UploadLog.log('header_rejected_client', {
                                reason: result.errorKey,
                                file: describeFile(result.file)
                            });
                            this.uppy.setFileState(result.file.id, {
                                error: this.$t(result.errorKey)
                            });
                            this.uppy.removeFile(result.file.id);
                        });

                    if(validFiles.length > 0) {
                        await this.uppy.upload();
                    }
                }).catch(error => {
                    UploadLog.logError('header_upload_failed', error);
                    this.showUploadError('upload_failed');
                }).finally(() => {
                    this.pendingValidations--;
                });

                return this.validationQueue;
            },
            checkForm: function() {
                if(this.pendingValidations > 0 || Object.keys(this.uppy.getState().currentUploads).length > 0) {
                    this.showUploadError('wait_for_upload');
                    return;
                }
                if(this.$store.getters.header_count >= 1) {
                    this.$store.dispatch('INCREMENT');
                } else {
                    this.isValid = false;
                    UploadLog.log('header_next_without_image');
                }
            }
        },
      mounted() {
            const uppy = new Uppy({
                debug: false,
                locale: Language.uppy,
                autoProceed: false,
                restrictions: {
                    maxFileSize: IMAGE_UPLOAD_RESTRICTIONS.maxFileSize,
                    maxNumberOfFiles: 1,
                    minNumberOfFiles: 1,
                    allowedFileTypes: getUppyAllowedFileTypes()
                },
                onBeforeFileAdded: file => {
                    const errorKey = validateImageMetadata(file);
                    if(errorKey) {
                        this.showUploadError(errorKey);
                        UploadLog.log('header_rejected_client', {
                            reason: errorKey,
                            file: describeFile(file)
                        });
                        return false;
                    }

                    this.uploadError = null;
                    return true;
                }
            });
            addBalancedImageCompression(uppy, Constants.IMAGE_COMPRESSION_OPTIONS, (file, error) => {
                UploadLog.log('header_compression_failed', {
                    file: describeFile(file),
                    message: error && error.message ? error.message : String(error)
                });
            })
                .use(XHRUpload, {
                  endpoint: Constants.API_URL + 'v2/upload/header',
                  formData: true,
                  fieldName: 'image'
                }).use(Dashboard, {
                  trigger: '.UppyModalOpenerBtn',
                  inline: true,
                  target: '#header-upload',
                  replaceTargetContent: true,
                  showProgressDetails: true,
                  hideUploadButton: true,
                  disableInformer: true,
                  note: this.$t('design_file_types'),
                  height: 340,
                  width: '100%',
                  browserBackButtonClose: false,
                  showRemoveButtonAfterComplete: true,
                  proudlyDisplayPoweredByUppy: false,
                  doneButtonHandler: null,
                  locale: {
                    strings: Language.dashboard.header
                  }
                });
            this.uppy = markRaw(uppy);
            limitDashboardFocusToKeyboard(this.uppy);
            this.uppy.on('file-removed', () => {
                this.$store.dispatch(types.RESET_HEADER);
            });

            this.uppy.on('files-added', files => {
                files.filter(file => file.source !== 'Remote').forEach(file => {
                    UploadLog.log('header_selected', { file: describeFile(file) });
                });
                this.validateAndUpload(files);
            });

            this.uppy.on('restriction-failed', (file, error) => {
                // Rejections from onBeforeFileAdded are already handled there.
                if(file && !(error && error.isUserFacing === false)) {
                    this.showUploadError(validateImageMetadata(file) || 'image_validation_file');
                    UploadLog.log('header_rejected_uppy', {
                        file: describeFile(file),
                        message: error && error.message ? error.message : String(error)
                    });
                }
            });

            this.uppy.on('upload-error', (file, error, response) => {
                const errorKey = getImageValidationErrorKey(error, response);
                this.showUploadError(errorKey || 'upload_failed');
                UploadLog.log('header_upload_error', {
                    file: describeFile(file),
                    status: response ? response.status : null,
                    message: error && error.message ? error.message : String(error),
                    response: response && typeof response.responseText === 'string' ? response.responseText.slice(0, 500) : null
                });
            });

            this.uppy.on('upload-success', (file, response) => {
                UploadLog.log('header_uploaded', { file: describeFile(file) });
                this.$store.dispatch(types.SET_HEADER_URL, {
                    preview: response.body.data
                });
                this.$store.dispatch(types.SET_HEADER_FILE, {
                    name: file.name,
                    type: file.type,
                    size: file.size
                });
                this.$store.dispatch(types.INCREMENT_HEADERCOUNT);

                if(Object.prototype.hasOwnProperty.call(window, 'dataLayer') && Array.isArray(window.dataLayer)) {
                    window.dataLayer.push({
                        'event': 'preview-main-image'
                    });
                }
            });

            if(this.$store.getters.preview != null && this.$store.getters.preview !== "") {
                this.uppy.setMeta({
                    code: this.$store.getters.preview
                });

                this.$nextTick(() => {
                    //Code fügt bestehendes Image nach einem reload wieder ein
                    if(this.$store.getters.header_url != null) {
                        this.$store.dispatch(types.RESET_HEADERCOUNT);
                        axios
                            .get(this.$store.getters.header_url, {responseType: "blob"})
                            .then(response => {
                                const preview =  URL.createObjectURL(response.data);
                                const headerFile = this.$store.getters.header_file || {};
                                const autoProceed = this.uppy.opts.autoProceed;
                                this.uppy.setOptions({autoProceed: false});

                                try {
                                    const fileId = this.uppy.addFile({
                                        name: headerFile.name || 'header.jpg',
                                        type: headerFile.type || 'image/jpeg',
                                        data: response.data,
                                        meta: {
                                            relativePath: null,
                                            compressed: true,
                                        },
                                        source: 'Remote',
                                        isRemote: false,
                                        isPaused: false
                                    });
                                    this.uppy.setFileState(fileId, {
                                        ...(headerFile.size && {
                                            size: headerFile.size,
                                        }),
                                        progress: {
                                            uploadComplete: true,
                                            uploadStarted: true,
                                        }
                                    });
                                    this.uppy.setFileState(fileId, { preview });
                                    if(this.uppy.getFiles().every(file => file.progress.uploadComplete)) {
                                        this.uppy.setState({totalProgress: 100});
                                    }
                                    this.$store.dispatch(types.INCREMENT_HEADERCOUNT);
                                } finally {
                                    this.uppy.setOptions({autoProceed});
                                }
                            }).catch(error => {
                                UploadLog.log('header_restore_failed', {
                                    status: error.response ? error.response.status : null,
                                    message: error.message
                                });
                            });
                    }
                });
            }
        },
        props: {
            preview: String,
            country: String,
        }
    }
</script>

<style scoped>

</style>
